import { Table } from 'antd';
import type { ColumnFilterItem, FilterDropdownProps } from 'antd/lib/table/interface';
import { pick, sortBy } from 'lodash/fp';
import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type LocalModelsListDataFragment } from '../../../api/fragments/LocalModelsListData';
import { useListLocalMakesQuery } from '../../../api/queries/listLocalMakes';
import { useListLocalModelsQuery } from '../../../api/queries/listLocalModels';
import { CompanyFilterListCollection } from '../../../api/types';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import FilterBox from '../../../components/FilterBox';
import PaginatedTableWithContext from '../../../components/PaginatedTableWithContext';
import InternalErrorResult from '../../../components/results/InternalErrorResult';
import useSearchInDropDown from '../../../components/useSearchInDropDown';
import renderBooleanIcon from '../../../utilities/renderBooleanIcon';
import renderFilterIcon from '../../../utilities/renderFilterIcon';
import renderSearchIcon from '../../../utilities/renderSearchIcon';
import useBodyTypeFilter from '../../../utilities/useBodyTypeFilter';
import useGetCompanyFilter from '../../../utilities/useGetCompanyFilter';
import useSortAndFilterCache, { SortAndFilterCacheKey } from '../../../utilities/useSortAndFilterCache';
import useListReducer from './useListReducer';
import useSortOrder, { getSortField } from './useSortOrder';

const ModelList = () => {
    const { t } = useTranslation('modelList');

    // get company context
    const company = useCompany(true);

    const navigate = useNavigate();

    const [currentCache, setCache] = useSortAndFilterCache(SortAndFilterCacheKey.Model);

    // get state from a reducer
    const [state, dispatch] = useListReducer(currentCache);

    // fetch data
    const { page, pageSize, sort, filter } = state;

    // get all makes for filter by model name
    const { data: allMakesData } = useListLocalMakesQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: { companyId: company?.id },
        },
    });

    const makeFilter = sortBy(
        'text',
        allMakesData?.list?.items.map(make => ({
            text: make.identifier,
            value: make.identifier,
        })) || []
    );

    const { data: allModelData } = useListLocalModelsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: { submodel: false, companyIds: company ? [company.id] : undefined },
        },
    });

    const allBodyTypeFilter = useBodyTypeFilter();
    const filteredBodyTypes = allBodyTypeFilter.filter(i =>
        (allModelData?.list?.items.map(i => i.bodyType) || []).includes(i.value)
    );

    const { data, loading, error } = useListLocalModelsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            pagination: { offset: (page - 1) * pageSize, limit: pageSize },
            sort: pick(['field', 'order'], sort),
            filter: { submodel: false, ...filter, companyIds: company ? [company.id] : filter.companyIds },
        },
    });

    const companyFilterList = useGetCompanyFilter({ collection: CompanyFilterListCollection.LocalModels });

    const onChange = useCallback(
        (pagination, filters, sorter, extra) => {
            switch (extra.action) {
                case 'sort':
                    return dispatch({
                        type: 'setSort',
                        sortBy: getSortField(sorter),
                    });

                case 'filter':
                    return dispatch({
                        type: 'setFilter',
                        filterBy: {
                            companyIds: !filters.company ? undefined : (filters.company as string[]),
                            name: !filters.name ? null : { defaultValue: filters.name[0], overrides: [] },
                            makeName: !filters.makeName ? null : filters.makeName,
                            identifier: !filters.identifier ? null : filters.identifier[0],
                            submodel: false,
                            bodyType: !filters.bodyType ? null : filters.bodyType,
                        },
                    });

                default:
                    return undefined;
            }
        },
        [dispatch]
    );

    // prepare items as a data source
    const dataSource = useMemo(() => (data?.list?.items || []).map(item => ({ ...item, key: item.id })), [data]);

    const total = data?.list?.count || 0;

    const searchBox = useSearchInDropDown();

    const showFilterDropDown = useCallback(
        (props: FilterDropdownProps, filters: ColumnFilterItem[]) =>
            FilterBox({
                filterDropDownProps: props,
                filters,
            }),
        []
    );

    const {
        companySort,
        nameSort,
        identifierSort,
        orderSort,
        activeSort,
        subModelSort,
        variantSort,
        makeSort,
        bodyTypeSort,
    } = useSortOrder(sort);

    if (!loading) {
        if (error) {
            return <InternalErrorResult />;
        }
    }

    return (
        <PaginatedTableWithContext
            company={company}
            dataSource={dataSource}
            dispatch={dispatch}
            loading={loading}
            onChange={onChange}
            onRow={(row: LocalModelsListDataFragment) => ({
                onClick: () => {
                    setCache(state);
                    navigate(`/admin/vehicles/models/${row.id}`);
                },
                style: { cursor: 'pointer' },
            })}
            rowKey="id"
            state={state}
            tableName={t('modelList:title')}
            total={total}
        >
            {!company && (
                <Table.Column
                    key="company"
                    dataIndex={['module', 'company', 'displayName']}
                    filterDropdown={props => showFilterDropDown(props, companyFilterList)}
                    filterIcon={renderFilterIcon}
                    filteredValue={filter.companyIds}
                    sortOrder={companySort}
                    title={t('modelList:columns.company')}
                    sorter
                />
            )}
            <Table.Column
                key="identifier"
                dataIndex="identifier"
                filterDropdown={searchBox.render('identifier')}
                filterIcon={renderSearchIcon}
                filteredValue={filter.identifier ? [filter.identifier] : undefined}
                onFilterDropdownOpenChange={searchBox.autoFocus}
                sortOrder={identifierSort}
                title={t('modelList:columns.identifier')}
                sorter
            />
            <Table.Column
                key="name"
                dataIndex="name"
                filterDropdown={searchBox.render('name')}
                filterIcon={renderSearchIcon}
                filteredValue={filter?.name?.defaultValue ? [filter?.name?.defaultValue] : undefined}
                onFilterDropdownOpenChange={searchBox.autoFocus}
                render={name => name.defaultValue}
                sortOrder={nameSort}
                title={t('modelList:columns.name')}
                sorter
            />
            <Table.Column
                key="submodels"
                dataIndex="submodels"
                render={submodels => submodels?.count}
                sortOrder={subModelSort}
                title={t('modelList:columns.submodels')}
                sorter
            />
            <Table.Column
                key="variants"
                dataIndex="variants"
                render={variants => variants?.count}
                sortOrder={variantSort}
                title={t('modelList:columns.variants')}
                sorter
            />
            <Table.Column
                key="makeName"
                dataIndex="make"
                filterDropdown={props => showFilterDropDown(props, makeFilter)}
                filterIcon={renderFilterIcon}
                filteredValue={filter.makeName}
                render={make => make.identifier}
                sortOrder={makeSort}
                title={t('modelList:columns.make')}
                sorter
            />
            <Table.Column
                key="order"
                dataIndex="order"
                sortOrder={orderSort}
                title={t('modelList:columns.order')}
                sorter
            />
            <Table.Column
                key="bodyType"
                dataIndex="bodyType"
                filterDropdown={props => showFilterDropDown(props, filteredBodyTypes)}
                filterIcon={renderFilterIcon}
                filteredValue={filter.bodyType}
                sortOrder={bodyTypeSort}
                title={t('modelList:columns.bodyType')}
                sorter
            />
            <Table.Column
                key="isActive"
                align="center"
                dataIndex="isActive"
                render={value => renderBooleanIcon(value, true)}
                sortOrder={activeSort}
                title={t('modelList:columns.isActive')}
                width={120}
                sorter
            />
        </PaginatedTableWithContext>
    );
};

export default ModelList;
